import { countActiveFilterSections } from '@sales-portal/domain/pricing/blocs/utils/pricing-filters';
import {
  PricingFilterScope,
  PricingFilterSection,
  PricingFilterSelections,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';
import { useCallback, useMemo } from 'react';
import { usePricingFiltersStore } from '@sales-portal/domain/pricing/blocs/stores/pricing-filters-store';
import { useShallow } from 'zustand/react/shallow';

export interface UsePricingFiltersResult extends PricingFilterSelections {
  activeCount: number;
  clearAll: () => void;
  clearSection: (sectionId: string) => void;
  hasActiveFilters: boolean;
  hydrate: (selections: PricingFilterSelections) => void;
  pruneSections: (allowedSectionIds: string[]) => void;
  setRangeBound: (
    sectionId: string,
    bound: 'max' | 'min',
    value: number | null,
  ) => void;
  togglePill: (sectionId: string, value: string, singleSelect: boolean) => void;
}

const EMPTY_SELECTIONS: PricingFilterSelections = { pills: {}, ranges: {} };

// Sections currently rendered in the sidebar — a bound or pill selection for
// a section that isn't in this list (e.g. its column was hidden) no longer
// applies, so it must not count toward activeCount either.
//
// `scope` isolates the shared store per screen so two screens with
// overlapping section ids (e.g. both use 'edits') never read or write each
// other's selections.
export const usePricingFilters = <T>(
  scope: PricingFilterScope,
  sections: PricingFilterSection<T>[],
): UsePricingFiltersResult => {
  const {
    selections,
    clearAllAction,
    clearSectionAction,
    hydrateAction,
    pruneSectionsAction,
    setRangeBoundAction,
    togglePillAction,
  } = usePricingFiltersStore(
    useShallow((state) => ({
      clearAllAction: state.clearAll,
      clearSectionAction: state.clearSection,
      hydrateAction: state.hydrate,
      pruneSectionsAction: state.pruneSections,
      selections: state.selections[scope] ?? EMPTY_SELECTIONS,
      setRangeBoundAction: state.setRangeBound,
      togglePillAction: state.togglePill,
    })),
  );

  const { pills, ranges } = selections;

  const clearAll = useCallback(
    () => clearAllAction(scope),
    [clearAllAction, scope],
  );
  const clearSection = useCallback(
    (sectionId: string) => clearSectionAction(scope, sectionId),
    [clearSectionAction, scope],
  );
  const hydrate = useCallback(
    (next: PricingFilterSelections) => hydrateAction(scope, next),
    [hydrateAction, scope],
  );
  const pruneSections = useCallback(
    (allowedSectionIds: string[]) =>
      pruneSectionsAction(scope, allowedSectionIds),
    [pruneSectionsAction, scope],
  );
  const setRangeBound = useCallback(
    (sectionId: string, bound: 'max' | 'min', value: number | null) =>
      setRangeBoundAction(scope, sectionId, bound, value),
    [setRangeBoundAction, scope],
  );
  const togglePill = useCallback(
    (sectionId: string, value: string, singleSelect: boolean) =>
      togglePillAction(scope, sectionId, value, singleSelect),
    [togglePillAction, scope],
  );

  const activeCount = useMemo(
    () => countActiveFilterSections({ pills, ranges }, sections),
    [pills, ranges, sections],
  );

  return {
    activeCount,
    clearAll,
    clearSection,
    hasActiveFilters: activeCount > 0,
    hydrate,
    pills,
    pruneSections,
    ranges,
    setRangeBound,
    togglePill,
  };
};
